
import React, { useState, useCallback } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { FileText, AlertCircle, Download, Info, Upload } from 'lucide-react'
import { useDropzone } from 'react-dropzone'
import { useToast } from "@/hooks/use-toast"
import { supabase } from "@/integrations/supabase/client"
import Papa from 'papaparse'
import * as XLSX from 'xlsx'

interface DomainUploadDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onUploadComplete: () => void
}

interface DomainData {
  domain: string
  status?: string
  daily_limit?: number
  notes?: string
  domain_registrar?: string
  mailbox_provider?: string
  client_name?: string
  smartlead_status?: boolean
  email_provider?: string
  csv_file_name?: string
  purchase_date?: string | null
  auto_renewal?: boolean
  renewal_date?: string | null
  expiry_date?: string | null
}

interface UploadResult {
  newDomains: DomainData[]
  duplicates: string[]
  errors: string[]
}

export function DomainUploadDialog({ open, onOpenChange, onUploadComplete }: DomainUploadDialogProps) {
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<DomainData[]>([])
  const [uploading, setUploading] = useState(false)
  const [errors, setErrors] = useState<string[]>([])
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null)
  const { toast } = useToast()

  const downloadTemplate = () => {
    const templateData = [
      {
        'domain': 'example.com',
        'client_name': 'Example Client',
        'domain_registrar': 'Hosting.de',
        'mailbox_provider': 'Superwave',
        'status': 'Active',
        'smartlead_status': 'true',
        'email_provider': 'Google',
        'purchase_date': '2024-08-08',
        'auto_renewal': 'true',
        'renewal_date': '2025-08-08',
        'expiry_date': '2025-08-08',
        'notes': 'Example domain entry'
      }
    ]
    
    const csv = Papa.unparse(templateData)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', 'domain_upload_template.csv')
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const onDrop = useCallback((acceptedFiles: File[]) => {
    console.log('🔄 Files dropped:', acceptedFiles)
    const uploadedFile = acceptedFiles[0]
    if (!uploadedFile) return

    setFile(uploadedFile)
    parseFile(uploadedFile)
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: false
  })

  const parseFile = (file: File) => {
    console.log('📄 Parsing file:', file.name)
    const fileExtension = file.name.split('.').pop()?.toLowerCase()

    if (fileExtension === 'csv') {
      Papa.parse(file, {
        header: true,
        complete: (results) => {
          console.log('✅ CSV parsed successfully:', results.data)
          const parsed = parseSimpleDomainData(results.data, file.name)
          setPreview(parsed.data)
          setErrors(parsed.errors)
        },
        error: (error) => {
          console.error('❌ CSV parsing error:', error)
          setErrors([`CSV parsing error: ${error.message}`])
        }
      })
    } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer)
          const workbook = XLSX.read(data, { type: 'array' })
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          const jsonData = XLSX.utils.sheet_to_json(worksheet)
          
          console.log('✅ Excel parsed successfully:', jsonData)
          const parsed = parseSimpleDomainData(jsonData, file.name)
          setPreview(parsed.data)
          setErrors(parsed.errors)
        } catch (error) {
          console.error('❌ Excel parsing error:', error)
          setErrors([`Excel parsing error: ${error instanceof Error ? error.message : 'Unknown error'}`])
        }
      }
      reader.readAsArrayBuffer(file)
    }
  }

  const normalizeDomain = (domain: string): string => {
    return domain.toLowerCase().trim().replace(/^www\./, '')
  }

  const toCapitalCase = (text: string | null | undefined): string | null => {
    if (!text || typeof text !== 'string') return text || null
    return text.trim()
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const parseSimpleDomainData = (rawData: any[], fileName: string): { data: DomainData[], errors: string[] } => {
    const data: DomainData[] = []
    const errors: string[] = []
    const seenDomains = new Set<string>()

    console.log('🔍 Parsing domains from raw data:', rawData.length, 'rows')

    rawData.forEach((row, index) => {
      try {
        // Check if the entire row is empty (skip completely empty rows)
        const isEmptyRow = Object.values(row).every(value => 
          value === null || value === undefined || String(value).trim() === ''
        )
        
        if (isEmptyRow) {
          console.log(`Skipping empty row ${index + 1}`)
          return
        }

        // Try different common domain column names
        const domainValue = row.domain || row.Domain || row.DOMAIN || row.domains || row.Domains || 
                           row['Domain Name'] || row['domain name'] || row.url || row.URL

        if (!domainValue) {
          errors.push(`Row ${index + 1}: Domain is required`)
          return
        }

        const normalizedDomain = normalizeDomain(String(domainValue))

        // Basic domain validation
        if (!normalizedDomain.includes('.') || normalizedDomain.startsWith('.') || normalizedDomain.endsWith('.')) {
          errors.push(`Row ${index + 1}: Invalid domain format: ${domainValue}`)
          return
        }

        // Check for duplicates within the file
        if (seenDomains.has(normalizedDomain)) {
          errors.push(`Row ${index + 1}: Duplicate domain in file: ${normalizedDomain}`)
          return
        }
        seenDomains.add(normalizedDomain)

        // Parse other common fields with better matching
        const client = row.client || row.Client || row.CLIENT || row.client_name || row['Client Name'] || 
                      row.company || row.Company
        const registrar = row.registrar || row.Registrar || row.domain_registrar || row['Domain Registrar'] ||
                         row['Domain Provider'] || row.provider
        const mailboxProvider = row.provider || row.mailbox_provider || row['Mailbox Provider'] || row.hosting || row.Hosting
        const status = row.status || row.Status || row.Active || row['Active?'] || 'Active'
        const smartlead = row.smartlead || row.Smartlead || row.smartlead_status || row['In Smartlead']
        const emailProvider = row.email_provider || row['Email Provider'] || row.emailprovider
        const notes = row.notes || row.Notes || row.note || row.Note || row.comments || row.Comments
        
        // Parse date fields
        const purchaseDate = row.purchase_date || row['Purchase Date'] || row.purchased || row.Purchased
        const autoRenewal = row.auto_renewal || row['Auto Renewal'] || row.renewal || row.Renewal
        const renewalDate = row.renewal_date || row['Renewal Date'] || row.expires || row.Expires
        const expiryDate = row.expiry_date || row['Expiry Date'] || row.expiration || row.Expiration

        data.push({
          domain: normalizedDomain, // Keep domain lowercase
          client_name: toCapitalCase(client),
          domain_registrar: toCapitalCase(registrar),
          mailbox_provider: toCapitalCase(mailboxProvider),
          status: toCapitalCase(status === 'ACTIVE' ? 'Active' : (status || 'Active')),
          smartlead_status: parseBooleanValue(smartlead),
          email_provider: toCapitalCase(emailProvider),
          notes: notes || null, // Keep notes as-is to preserve formatting
          csv_file_name: fileName,
          daily_limit: 500,
          purchase_date: formatDateForDatabase(purchaseDate),
          auto_renewal: parseBooleanValue(autoRenewal),
          renewal_date: formatDateForDatabase(renewalDate),
          expiry_date: formatDateForDatabase(expiryDate)
        })
      } catch (error) {
        errors.push(`Row ${index + 1}: ${error instanceof Error ? error.message : 'Parse error'}`)
      }
    })

    console.log(`📊 Parsed ${data.length} valid domains, ${errors.length} errors`)
    return { data, errors }
  }

  const formatDateForDatabase = (dateValue: any): string | null => {
    if (!dateValue || dateValue === '') return null
    
    const dateString = String(dateValue).trim()
    
    try {
      // Handle DD/MM/YYYY format (convert to YYYY-MM-DD for PostgreSQL)
      if (dateString.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
        const [day, month, year] = dateString.split('/')
        const dayNum = parseInt(day)
        const monthNum = parseInt(month)
        
        // Validate day and month ranges
        if (dayNum < 1 || dayNum > 31 || monthNum < 1 || monthNum > 12) {
          console.warn('Invalid date values:', { day: dayNum, month: monthNum, year })
          return null
        }
        
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
      }
      
      // Handle DD-MM-YYYY format (convert to YYYY-MM-DD for PostgreSQL)
      if (dateString.match(/^\d{1,2}-\d{1,2}-\d{4}$/)) {
        const [day, month, year] = dateString.split('-')
        const dayNum = parseInt(day)
        const monthNum = parseInt(month)
        
        // Validate day and month ranges
        if (dayNum < 1 || dayNum > 31 || monthNum < 1 || monthNum > 12) {
          console.warn('Invalid date values:', { day: dayNum, month: monthNum, year })
          return null
        }
        
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
      }
      
      // Handle MM/DD/YYYY format (convert to YYYY-MM-DD for PostgreSQL)
      if (dateString.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/) && parseInt(dateString.split('/')[0]) <= 12) {
        const [month, day, year] = dateString.split('/')
        const dayNum = parseInt(day)
        const monthNum = parseInt(month)
        
        // Validate day and month ranges
        if (dayNum < 1 || dayNum > 31 || monthNum < 1 || monthNum > 12) {
          console.warn('Invalid date values:', { day: dayNum, month: monthNum, year })
          return null
        }
        
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
      }
      
      // Handle YYYY-MM-DD format (already correct for PostgreSQL)
      if (dateString.match(/^\d{4}-\d{1,2}-\d{1,2}$/)) {
        const [year, month, day] = dateString.split('-')
        const dayNum = parseInt(day)
        const monthNum = parseInt(month)
        
        // Validate day and month ranges
        if (dayNum < 1 || dayNum > 31 || monthNum < 1 || monthNum > 12) {
          console.warn('Invalid date values:', { day: dayNum, month: monthNum, year })
          return null
        }
        
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
      }
      
      // Try to parse other formats and convert to YYYY-MM-DD for PostgreSQL
      const parsedDate = new Date(dateString)
      if (!isNaN(parsedDate.getTime())) {
        const day = parsedDate.getDate().toString().padStart(2, '0')
        const month = (parsedDate.getMonth() + 1).toString().padStart(2, '0')
        const year = parsedDate.getFullYear()
        return `${year}-${month}-${day}`
      }
      
      return null
    } catch (error) {
      console.warn('Date parsing error for value:', dateValue, error)
      return null
    }
  }

  const parseBooleanValue = (value: any): boolean => {
    if (value === undefined || value === null || value === '') return false
    if (typeof value === 'boolean') return value
    if (typeof value === 'string') {
      const lower = value.toLowerCase().trim()
      return lower === 'true' || lower === 'yes' || lower === '1' || lower === 'active' || 
             lower === '✓' || lower === 'x' || lower === 'on'
    }
    return false
  }

  const checkForExistingDomains = async (domains: DomainData[]): Promise<UploadResult> => {
    console.log('🔍 Checking for existing domains...')
    
    try {
      const domainNames = domains.map(d => normalizeDomain(d.domain))
      console.log('📋 Domains to check:', domainNames)
      
      const { data: existingDomains, error } = await supabase
        .from('domains')
        .select('domain')
        .in('domain', domainNames)

      if (error) {
        console.error('❌ Error checking existing domains:', error)
        throw error
      }

      console.log('🔍 Found existing domains in database:', existingDomains?.map(d => d.domain) || [])
      
      const existingDomainNames = new Set(existingDomains?.map(d => normalizeDomain(d.domain)) || [])
      const updatingDomains = domains.filter(d => existingDomainNames.has(normalizeDomain(d.domain)))
      const newDomains = domains.filter(d => !existingDomainNames.has(normalizeDomain(d.domain)))

      console.log(`📊 Found ${updatingDomains.length} existing domains to update, ${newDomains.length} new domains to add`)
      console.log('🔄 Domains to update:', updatingDomains.map(d => d.domain))
      console.log('✅ New domains to add:', newDomains.map(d => d.domain))

      return {
        newDomains: domains, // Return all domains (will use upsert, so both new and existing)
        duplicates: [], // No longer skipping duplicates, will update them
        errors: []
      }
    } catch (error) {
      console.error('❌ Error checking existing domains:', error)
      return {
        newDomains: [],
        duplicates: [],
        errors: [`Error checking existing domains: ${error instanceof Error ? error.message : 'Unknown error'}`]
      }
    }
  }

  const handleUpload = async () => {
    if (!preview.length) return

    setUploading(true)
    try {
      console.log('🚀 Starting upload process...')
      
      // Check for existing domains
      const result = await checkForExistingDomains(preview)
      
      if (result.errors.length > 0) {
        setErrors(result.errors)
        return
      }

      setUploadResult(result)

      // Upload/update all domains using upsert (will insert new or update existing)
      if (result.newDomains.length > 0) {
        console.log('📤 Uploading/updating domains:', result.newDomains.map(d => d.domain))
        
        // Normalize domains for upsert
        const domainsToUpsert = result.newDomains.map(d => ({
          ...d,
          domain: normalizeDomain(d.domain)
        }))
        
        const { data, error } = await supabase
          .from('domains')
          .upsert(domainsToUpsert, {
            onConflict: 'domain',
            ignoreDuplicates: false // Update existing domains instead of ignoring
          })
          .select()

        if (error) {
          console.error('❌ Supabase upsert error:', error)
          throw new Error(`Database error: ${error.message} (Code: ${error.code})`)
        }

        const insertedCount = data?.length || 0
        console.log(`✅ Successfully processed ${insertedCount} domains (inserted new and/or updated existing):`, data)
      }

      // Show success message with details
      let successMessage = `Upload complete!`
      if (result.newDomains.length > 0) {
        successMessage += ` Processed ${result.newDomains.length} domain${result.newDomains.length !== 1 ? 's' : ''} (inserted new and/or updated existing).`
      }

      toast({
        title: "Success",
        description: successMessage
      })
      
      onUploadComplete()
      resetDialog()
    } catch (error) {
      console.error('❌ Upload error:', error)
      
      // Better error message
      let errorMessage = "Unknown error occurred"
      if (error instanceof Error) {
        errorMessage = error.message
      }
      
      setErrors([errorMessage])
      
      toast({
        title: "Upload Failed",
        description: errorMessage,
        variant: "destructive"
      })
    } finally {
      setUploading(false)
    }
  }

  const resetDialog = () => {
    setFile(null)
    setPreview([])
    setErrors([])
    setUploadResult(null)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload Domains</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Template Download */}
          <div className="flex justify-between items-center">
            <Label>Upload CSV or Excel File</Label>
            <Button variant="outline" size="sm" onClick={downloadTemplate}>
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
          </div>
          
          {/* File Upload Area */}
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
              isDragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <input {...getInputProps()} />
            <FileText className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            {file ? (
              <p className="text-sm font-medium">{file.name}</p>
            ) : (
              <div>
                <p className="text-sm font-medium">Drop your file here, or click to browse</p>
                <p className="text-xs text-gray-500 mt-1">Supports CSV, XLS, and XLSX files</p>
                <p className="text-xs text-gray-500 mt-1">Required: domain column. Optional: client, registrar, provider, status, etc.</p>
              </div>
            )}
          </div>

          {/* Errors */}
          {errors.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="font-medium mb-2">Please fix the following errors:</div>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  {errors.slice(0, 10).map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                  {errors.length > 10 && (
                    <li>... and {errors.length - 10} more errors</li>
                  )}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Upload Result Info */}
          {uploadResult && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                <div className="font-medium mb-2">Upload Results:</div>
                <ul className="space-y-1 text-sm">
                  {uploadResult.newDomains.length > 0 && (
                    <li>✅ {uploadResult.newDomains.length} domain{uploadResult.newDomains.length !== 1 ? 's' : ''} will be processed (inserted new or updated existing)</li>
                  )}
                </ul>
                <p className="text-xs text-muted-foreground mt-2">
                  Existing domains will be updated with new data from the file. New domains will be inserted.
                </p>
              </AlertDescription>
            </Alert>
          )}

          {/* Preview */}
          {preview.length > 0 && errors.length === 0 && (
            <div>
              <Label>Preview ({preview.length} domains)</Label>
              <div className="mt-2 border rounded-lg max-h-60 overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left">Domain</th>
                      <th className="px-3 py-2 text-left">Client</th>
                      <th className="px-3 py-2 text-left">Registrar</th>
                      <th className="px-3 py-2 text-left">Provider</th>
                      <th className="px-3 py-2 text-left">Status</th>
                      <th className="px-3 py-2 text-left">Smartlead</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.slice(0, 50).map((domain, index) => (
                      <tr key={index} className="border-b">
                        <td className="px-3 py-2">{domain.domain}</td>
                        <td className="px-3 py-2">{domain.client_name || '-'}</td>
                        <td className="px-3 py-2">{domain.domain_registrar || '-'}</td>
                        <td className="px-3 py-2">{domain.mailbox_provider || '-'}</td>
                        <td className="px-3 py-2">{domain.status}</td>
                        <td className="px-3 py-2">{domain.smartlead_status ? 'Yes' : 'No'}</td>
                      </tr>
                    ))}
                    {preview.length > 50 && (
                      <tr>
                        <td colSpan={6} className="px-3 py-2 text-center text-gray-500">
                          ... and {preview.length - 50} more rows
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpload}
              disabled={preview.length === 0 || errors.length > 0 || uploading}
            >
              {uploading ? 'Uploading...' : `Upload ${preview.length} Domains`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
